import {
  Component,
  createEffect,
  JSXElement,
  mergeProps,
  Show,
  untrack
} from 'solid-js';
import { OrgDetailsDataType } from '~/components/AppRouter/utils.ts';
import SkLoader from '~/components/ui/SkLoader';
import OrganizationContextProvider, {
  useOrganizationContext
} from '~/components/AppData/OrganizationContext.tsx';
import { useNeedHelpContext } from '~/components/Layout/NeedHelp/NeedHelpContext.tsx';
import { getIdpDocLink } from '~/pages/Authentication/Connections/utils.ts';
import { OrganizationData } from '~/api';
import {
  DIRECTORY_PROVIDERS_WITH_DOC_LINK,
  getDirectoryProviderDocLink
} from '~/pages/DirectorySync/utils.ts';

type AdminPortalWithOrganizationContextProps = {
  children: JSXElement;
  type: OrgDetailsDataType;
  organization: OrganizationData | undefined;
};

function ComponentWithNeedHelpContextData(props: {
  type: OrgDetailsDataType;
  children: JSXElement;
}) {
  const merged = mergeProps({}, props);
  const orgData = useOrganizationContext();
  const needHelpContext = useNeedHelpContext();

  createEffect(() => {
    if (merged.type === OrgDetailsDataType.SSO_CONNECTIONS) {
      const connection = orgData.selectedConnection;
      if (connection?.provider) {
        untrack(() => {
          needHelpContext.setShouldShowHelp(true);
          needHelpContext.setHelpDocLink(getIdpDocLink(connection));
        });
        return;
      }
    }

    if (merged.type === OrgDetailsDataType.DIRECTORIES) {
      const directory = orgData.selectedDirectory;
      const provider = directory?.directoryProvider;
      if (provider && DIRECTORY_PROVIDERS_WITH_DOC_LINK.includes(provider)) {
        untrack(() => {
          needHelpContext.setShouldShowHelp(true);
          needHelpContext.setHelpDocLink(
            getDirectoryProviderDocLink(directory)
          );
        });
        return;
      }
    }

    untrack(() => {
      needHelpContext.setShouldShowHelp(false);
      needHelpContext.setHelpDocLink('');
    });
  });

  return <>{merged.children}</>;
}

function AdminPortalWithOrganizationContext(
  props: AdminPortalWithOrganizationContextProps
): JSXElement {
  const merged = mergeProps({}, props);
  return (
    <OrganizationContextProvider
      type={merged.type}
      organization={merged.organization}
    >
      {(isDataLoaded) => (
        <Show
          when={isDataLoaded}
          fallback={<SkLoader />}
        >
          <ComponentWithNeedHelpContextData type={merged.type}>
            {merged.children}
          </ComponentWithNeedHelpContextData>
        </Show>
      )}
    </OrganizationContextProvider>
  );
}

export default function withOrganizationContext(
  component: Component,
  organization: OrganizationData | undefined,
  type: OrgDetailsDataType
) {
  const Renderer = component;
  return () => (
    <AdminPortalWithOrganizationContext
      organization={organization}
      type={type}
    >
      <Renderer />
    </AdminPortalWithOrganizationContext>
  );
}
